'use client';

import { useEffect } from 'react';
import { useAchievementNotifications } from '@/lib/hooks/training/useAchievementNotifications';
import { useUser } from '@/components/shell/AppShellContext';
import type { Rarity } from '@/lib/training/types';
import { showUnifiedToast } from '@/lib/notifications/show-unified-toast';

/** Map rarity to Tailwind color stem for the unified toast */
const RARITY_ACCENT: Record<Rarity, string> = {
  common: 'gray-500',
  uncommon: 'green-500',
  rare: 'blue-500',
  epic: 'purple-500',
  legendary: 'amber-400',
};

export function TrainingAchievementProvider({ children }: { children: React.ReactNode }) {
  const user = useUser();
  const { latestEvent, clearLatest } = useAchievementNotifications(user?.id ?? null);

  useEffect(() => {
    if (!latestEvent) return;

    const metadata = latestEvent.metadata || {};
    const title = (metadata.title as string) || 'Achievement Unlocked!';
    const icon = (metadata.icon as string) || '🏆';
    const rarity = (metadata.rarity as Rarity) || 'common';
    const points = (metadata.points as number) || 0;

    showUnifiedToast({
      variant: 'achievement',
      title,
      icon,
      accentColor: RARITY_ACCENT[rarity],
      categoryLabel: 'Achievement Unlocked!',
      description: `${rarity.charAt(0).toUpperCase() + rarity.slice(1)} · +${points} pts`,
      duration: 5000,
    });

    clearLatest();
  }, [latestEvent, clearLatest]);

  return <>{children}</>;
}
